// frontend/src/components/DateTimeField12h.js

import React, { useMemo } from "react";

/**
 * 12-hour date+time picker
 * - value: "" or something parsable by new Date(value)
 * - onChange: (string) => void  // emits "YYYY-MM-DDTHH:MM" (local)
 * - minuteStep: number (default 5)
 * - className: string
 */
export default function DateTimeField12h({ value = "", onChange, minuteStep = 5, className = "" }) {
  const d = useMemo(() => (value ? new Date(value) : new Date()), [value]);

  const pad2 = (n) => String(n).padStart(2, "0");

  // Build current parts (local)
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const dateStr = `${yyyy}-${mm}-${dd}`;

  let hour24 = d.getHours();
  const minute = d.getMinutes();
  const am = hour24 < 12;
  const hour12 = ((hour24 + 11) % 12) + 1; // 0->12, 13->1 etc.

  // Options
  const hours12 = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => i * minuteStep);

  const emit = (nd) => {
    const y = nd.getFullYear();
    const m = pad2(nd.getMonth() + 1);
    const d2 = pad2(nd.getDate());
    const h = pad2(nd.getHours());
    const mi = pad2(nd.getMinutes());
    onChange?.(`${y}-${m}-${d2}T${h}:${mi}`);
  };

  const changeDate = (newDateStr) => {
    const parts = newDateStr.split("-");
    if (parts.length !== 3) return;
    const nd = new Date(d);
    nd.setFullYear(Number(parts[0]));
    nd.setMonth(Number(parts[1]) - 1);
    nd.setDate(Number(parts[2]));
    emit(nd);
  };

  const changeHour12 = (newHour12) => {
    const h12 = Number(newHour12);
    const wasAm = d.getHours() < 12;
    const h24 = (h12 % 12) + (wasAm ? 0 : 12);
    const nd = new Date(d);
    nd.setHours(h24);
    emit(nd);
  };

  const changeMinute = (newMin) => {
    const nd = new Date(d);
    nd.setMinutes(Number(newMin));
    emit(nd);
  };

  const toggleAmPm = (next) => {
    const nd = new Date(d);
    const cur = nd.getHours();
    if (next === "AM" && cur >= 12) nd.setHours(cur - 12);
    if (next === "PM" && cur < 12) nd.setHours(cur + 12);
    emit(nd);
  };

  return (
    <div className={`flex w-full gap-1.5 md:gap-2 ${className}`}>
      <input
        type="date"
        value={dateStr}
        onChange={(e) => changeDate(e.target.value)}
        className="!min-w-0 !min-h-0 flex-[1.6] text-sm border rounded-2xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
      />

      <select
        value={hour12}
        onChange={(e) => changeHour12(e.target.value)}
        className="!min-w-0 !min-h-0 flex-[0.55] text-sm border rounded-2xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
        aria-label="Hour"
      >
        {hours12.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>

      <select
        value={minutes.includes(minute) ? minute : minutes[0]}
        onChange={(e) => changeMinute(e.target.value)}
        className="!min-w-0 !min-h-0 flex-[0.65] text-sm border rounded-2xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
        aria-label="Minute"
      >
        {minutes.map((m) => (
          <option key={m} value={m}>
            {m.toString().padStart(2, "0")}
          </option>
        ))}
      </select>

      <div className="flex min-w-0 flex-[1.25] gap-1 md:gap-2">
        <button
          type="button"
          onClick={() => toggleAmPm("AM")}
          className={`flex-1 !min-w-0 !min-h-0 flex-1 border rounded-2xl px-1.5 py-1.5 text-sm ${
            am
              ? "bg-blue-600 text-white border-blue-600"
              : "hover:bg-gray-50"
          }`}
          aria-pressed={am}
        >
          AM
        </button>

        <button
          type="button"
          onClick={() => toggleAmPm("PM")}
          className={`flex-1 !min-w-0 !min-h-0 flex-1 border rounded-2xl px-1.5 py-1.5 text-sm ${
            !am
              ? "bg-blue-600 text-white border-blue-600"
              : "hover:bg-gray-50"
          }`}
          aria-pressed={!am}
        >
          PM
        </button>
      </div>
    </div>
  );
}
